/**
 * Learning Components Library
 * Comprehensive collection of interactive learning components for different skill areas
 */

export const LEARNING_COMPONENTS_LIBRARY = [
  // ==================== EXISTING LEARN FOR FUN FEATURES ====================
  {
    name: "Code Canvas",
    description: "Interactive turtle graphics programming canvas for creative coding and visual art",
    category: "Creative Coding",
    type: "creative",
    config: {
      turtleGraphics: true,
      drawingCommands: ["forward", "backward", "left", "right", "penup", "pendown"],
      colors: true,
      shapes: true,
      animations: true,
      coordinateSystem: "cartesian"
    },
    settings: {
      canvasSize: "800x600",
      maxCommands: 1000,
      showGrid: true,
      allowUndo: true,
      saveProject: true
    },
    preview: "Create art and patterns using turtle graphics programming",
    difficulty: "beginner",
    estimatedTime: 25,
    tags: ["turtle", "graphics", "creative", "programming", "art"],
    existingFeature: true,
    route: "/code-canvas"
  },
  {
    name: "Code TikTok",
    description: "Vertical swipe interface for discovering and sharing code snippets with social features",
    category: "Social Learning",
    type: "collaborative",
    config: {
      swipeNavigation: true,
      codeSnippets: true,
      socialFeatures: ["likes", "comments", "shares", "remixes"],
      categories: ["javascript", "python", "web", "algorithms"],
      trending: true
    },
    settings: {
      autoPlay: true,
      showCode: true,
      allowComments: true,
      dailyLimit: 50,
      saveFavorites: true
    },
    preview: "Discover and share code snippets in TikTok-style vertical feed",
    difficulty: "beginner",
    estimatedTime: 15,
    tags: ["social", "snippets", "discovery", "community", "mobile"],
    existingFeature: true,
    route: "/code-tiktok"
  },
  {
    name: "Marketplace",
    description: "Multi-instructor marketplace for courses and sessions with enrollment and reviews",
    category: "Marketplace",
    type: "collaborative",
    config: {
      courseListing: true,
      instructorProfiles: true,
      enrollment: true,
      reviews: true,
      search: true,
      categories: true,
      pricing: true
    },
    settings: {
      allowReviews: true,
      requireApproval: true,
      commissionRate: 0.3,
      featuredCourses: true
    },
    preview: "Browse and enroll in courses from multiple instructors",
    difficulty: "beginner",
    estimatedTime: 20,
    tags: ["marketplace", "courses", "enrollment", "reviews", "commerce"],
    existingFeature: true,
    route: "/marketplace"
  },
  {
    name: "Metaverse World",
    description: "3D virtual world for collaborative learning with avatars and interactive zones",
    category: "Virtual Reality",
    type: "collaborative",
    config: {
      threeDWorld: true,
      avatars: true,
      zones: ["classroom", "lab", "creative", "social"],
      realTimeChat: true,
      voiceChat: true,
      interactiveObjects: true
    },
    settings: {
      maxUsers: 50,
      voiceEnabled: true,
      screenSharing: true,
      privateZones: true
    },
    preview: "Learn and collaborate in immersive 3D virtual environments",
    difficulty: "intermediate",
    estimatedTime: 30,
    tags: ["metaverse", "3d", "virtual", "collaboration", "immersive"],
    existingFeature: true,
    route: "/metaverse"
  },
  {
    name: "Neuro-Adaptive Learning",
    description: "AI-powered learning system that adapts to individual behavior and performance patterns",
    category: "AI Learning",
    type: "assessment",
    config: {
      behaviorTracking: ["typing", "mouse", "focus", "errors"],
      adaptationEngine: true,
      personalizedContent: true,
      difficultyAdjustment: true,
      learningPaths: true
    },
    settings: {
      trackingEnabled: true,
      privacyMode: true,
      adaptationRate: "medium",
      reportFrequency: "weekly"
    },
    preview: "Personalized learning that adapts to your behavior and performance",
    difficulty: "advanced",
    estimatedTime: 45,
    tags: ["ai", "adaptive", "personalization", "analytics", "neuroscience"],
    existingFeature: true,
    integrated: true
  },

  // ==================== CODING & TECHNOLOGY ====================
  {
    name: "Code Editor",
    description: "Interactive code editor with syntax highlighting and real-time preview",
    category: "Coding",
    type: "interactive",
    config: {
      languages: ["javascript", "python", "html", "css"],
      themes: ["light", "dark"],
      autoComplete: true,
      lineNumbers: true,
      livePreview: true
    },
    settings: {
      maxCodeLength: 10000,
      allowFileUpload: false,
      showConsole: true
    },
    preview: "Live coding environment with syntax highlighting",
    difficulty: "beginner",
    estimatedTime: 15,
    tags: ["coding", "programming", "editor", "interactive"]
  },
  {
    name: "Code Challenge",
    description: "Programming challenges with automated testing and feedback",
    category: "Coding",
    type: "assessment",
    config: {
      challengeTypes: ["algorithm", "debug", "optimization"],
      testCases: true,
      hints: true,
      solutionReveal: false
    },
    settings: {
      maxAttempts: 3,
      timeLimit: 1800, // 30 minutes
      showScore: true
    },
    preview: "Solve coding challenges with instant feedback",
    difficulty: "intermediate",
    estimatedTime: 20,
    tags: ["coding", "challenge", "assessment", "problem-solving"]
  },
  {
    name: "Code Review",
    description: "Peer code review system with feedback and rating",
    category: "Coding",
    type: "collaborative",
    config: {
      reviewTypes: ["syntax", "logic", "style", "efficiency"],
      anonymous: false,
      ratingScale: 5
    },
    settings: {
      minReviewLength: 50,
      requiredReviews: 2,
      allowSelfReview: false
    },
    preview: "Review and provide feedback on peer code",
    difficulty: "intermediate",
    estimatedTime: 10,
    tags: ["coding", "review", "collaboration", "feedback"]
  },
  {
    name: "API Explorer",
    description: "Interactive API testing tool with request builder and response viewer",
    category: "Technology",
    type: "interactive",
    config: {
      methods: ["GET", "POST", "PUT", "DELETE"],
      headers: true,
      bodyTypes: ["json", "form", "raw"],
      responseFormat: ["json", "xml", "text"]
    },
    settings: {
      maxRequestSize: 1024,
      timeout: 10000,
      saveHistory: true
    },
    preview: "Test APIs with interactive request builder",
    difficulty: "advanced",
    estimatedTime: 15,
    tags: ["api", "testing", "web", "development"]
  },
  {
    name: "Database Designer",
    description: "Visual database schema designer with relationship mapping",
    category: "Technology",
    type: "interactive",
    config: {
      databaseTypes: ["mysql", "postgresql", "mongodb"],
      relationshipTypes: ["one-to-one", "one-to-many", "many-to-many"],
      exportFormats: ["sql", "json", "diagram"]
    },
    settings: {
      maxTables: 20,
      allowSqlGeneration: true,
      validation: true
    },
    preview: "Design database schemas visually",
    difficulty: "intermediate",
    estimatedTime: 25,
    tags: ["database", "design", "schema", "development"]
  },

  // ==================== CREATIVE ARTS ====================
  {
    name: "Drawing Canvas",
    description: "Interactive drawing canvas with tools and layers",
    category: "Creative Arts",
    type: "creative",
    config: {
      tools: ["pencil", "brush", "eraser", "shapes", "text"],
      colors: true,
      layers: true,
      exportFormats: ["png", "jpg", "svg"]
    },
    settings: {
      maxLayers: 10,
      canvasSize: "800x600",
      undoSteps: 50
    },
    preview: "Create digital art with drawing tools",
    difficulty: "beginner",
    estimatedTime: 20,
    tags: ["art", "drawing", "creative", "visual"]
  },
  {
    name: "Music Composer",
    description: "Create music with virtual instruments and notation",
    category: "Creative Arts",
    type: "creative",
    config: {
      instruments: ["piano", "guitar", "drums", "synthesizer"],
      notation: true,
      tempo: true,
      exportFormats: ["mp3", "midi", "sheet"]
    },
    settings: {
      maxTracks: 8,
      maxDuration: 180, // 3 minutes
      allowRecording: true
    },
    preview: "Compose music with virtual instruments",
    difficulty: "intermediate",
    estimatedTime: 30,
    tags: ["music", "composition", "creative", "audio"]
  },
  {
    name: "Video Editor",
    description: "Simple video editing with timeline and effects",
    category: "Creative Arts",
    type: "creative",
    config: {
      editingTools: ["cut", "trim", "merge", "effects"],
      transitions: true,
      audioTracks: true,
      exportFormats: ["mp4", "webm"]
    },
    settings: {
      maxVideoLength: 300, // 5 minutes
      maxClips: 20,
      resolution: "1080p"
    },
    preview: "Edit videos with timeline and effects",
    difficulty: "intermediate",
    estimatedTime: 25,
    tags: ["video", "editing", "creative", "multimedia"]
  },
  {
    name: "3D Modeler",
    description: "Basic 3D modeling with primitives and transformations",
    category: "Creative Arts",
    type: "interactive",
    config: {
      primitives: ["cube", "sphere", "cylinder", "cone"],
      transformations: ["move", "rotate", "scale"],
      materials: true,
      lighting: true
    },
    settings: {
      maxObjects: 50,
      exportFormats: ["obj", "stl"],
      renderQuality: "medium"
    },
    preview: "Create 3D models with basic tools",
    difficulty: "advanced",
    estimatedTime: 30,
    tags: ["3d", "modeling", "creative", "design"]
  },
  {
    name: "Animation Studio",
    description: "Create frame-by-frame animations with timeline",
    category: "Creative Arts",
    type: "creative",
    config: {
      animationTypes: ["frame-by-frame", "tween", "path"],
      layers: true,
      onionSkin: true,
      exportFormats: ["gif", "mp4", "spritesheet"]
    },
    settings: {
      maxFrames: 120,
      fps: 24,
      canvasSize: "800x600"
    },
    preview: "Create animations with timeline tools",
    difficulty: "intermediate",
    estimatedTime: 35,
    tags: ["animation", "creative", "visual", "timeline"]
  },

  // ==================== GAMING & INTERACTIVITY ====================
  {
    name: "Game Builder",
    description: "Create simple games with drag-and-drop interface",
    category: "Gaming",
    type: "interactive",
    config: {
      gameTypes: ["platformer", "puzzle", "quiz", "adventure"],
      assets: true,
      scripting: "visual",
      physics: true
    },
    settings: {
      maxLevels: 10,
      maxSprites: 50,
      allowCustomCode: false
    },
    preview: "Build games with visual scripting",
    difficulty: "intermediate",
    estimatedTime: 40,
    tags: ["gaming", "development", "interactive", "creative"]
  },
  {
    name: "Quiz Master",
    description: "Create and take interactive quizzes with multiple question types",
    category: "Gaming",
    type: "assessment",
    config: {
      questionTypes: ["multiple-choice", "true-false", "fill-blank", "matching"],
      mediaSupport: true,
      timeLimits: true,
      instantFeedback: true
    },
    settings: {
      maxQuestions: 50,
      randomOrder: true,
      showResults: true
    },
    preview: "Create interactive quizzes with instant feedback",
    difficulty: "beginner",
    estimatedTime: 15,
    tags: ["quiz", "assessment", "interactive", "learning"]
  },
  {
    name: "Virtual Lab",
    description: "Simulated experiments for science and engineering",
    category: "Gaming",
    type: "interactive",
    config: {
      experimentTypes: ["physics", "chemistry", "biology", "engineering"],
      measurements: true,
      dataLogging: true,
      safetyMode: true
    },
    settings: {
      maxVariables: 10,
      precision: 2,
      allowReset: true
    },
    preview: "Conduct virtual experiments safely",
    difficulty: "intermediate",
    estimatedTime: 30,
    tags: ["science", "simulation", "experiment", "interactive"]
  },
  {
    name: "Story Creator",
    description: "Interactive story builder with branching narratives",
    category: "Gaming",
    type: "creative",
    config: {
      storyTypes: ["adventure", "mystery", "educational", "fantasy"],
      branching: true,
      characters: true,
      illustrations: true
    },
    settings: {
      maxChapters: 20,
      maxChoices: 4,
      allowCollaboration: true
    },
    preview: "Create interactive branching stories",
    difficulty: "beginner",
    estimatedTime: 25,
    tags: ["story", "creative", "writing", "interactive"]
  },

  // ==================== COLLABORATION & COMMUNICATION ====================
  {
    name: "Whiteboard",
    description: "Collaborative whiteboard with drawing and text tools",
    category: "Collaboration",
    type: "collaborative",
    config: {
      tools: ["pen", "eraser", "text", "shapes", "sticky-notes"],
      realTime: true,
      chat: true,
      exportFormats: ["png", "pdf"]
    },
    settings: {
      maxUsers: 10,
      autoSave: true,
      versionHistory: true
    },
    preview: "Collaborate on shared whiteboard",
    difficulty: "beginner",
    estimatedTime: 20,
    tags: ["collaboration", "whiteboard", "drawing", "real-time"]
  },
  {
    name: "Discussion Forum",
    description: "Threaded discussions with voting and moderation",
    category: "Collaboration",
    type: "collaborative",
    config: {
      postTypes: ["question", "discussion", "announcement"],
      voting: true,
      attachments: true,
      moderation: true
    },
    settings: {
      maxPostLength: 5000,
      allowAnonymous: false,
      emailNotifications: true
    },
    preview: "Engage in threaded discussions",
    difficulty: "beginner",
    estimatedTime: 10,
    tags: ["discussion", "forum", "collaboration", "communication"]
  },
  {
    name: "Peer Review",
    description: "Structured peer review system with rubrics",
    category: "Collaboration",
    type: "assessment",
    config: {
      reviewTypes: ["project", "presentation", "writing", "code"],
      rubrics: true,
      anonymous: true,
      feedback: true
    },
    settings: {
      requiredReviews: 3,
      reviewDeadline: true,
      qualityScore: true
    },
    preview: "Review peer work with structured rubrics",
    difficulty: "intermediate",
    estimatedTime: 15,
    tags: ["review", "peer", "assessment", "feedback"]
  },
  {
    name: "Team Project",
    description: "Collaborative project management with tasks and roles",
    category: "Collaboration",
    type: "collaborative",
    config: {
      taskManagement: true,
      roleAssignment: true,
      progressTracking: true,
      fileSharing: true
    },
    settings: {
      maxTeamSize: 8,
      taskDependencies: true,
      milestoneTracking: true
    },
    preview: "Manage team projects collaboratively",
    difficulty: "intermediate",
    estimatedTime: 30,
    tags: ["project", "team", "collaboration", "management"]
  },

  // ==================== ASSESSMENT & FEEDBACK ====================
  {
    name: "Skill Assessment",
    description: "Comprehensive skill evaluation with adaptive testing",
    category: "Assessment",
    type: "assessment",
    config: {
      assessmentTypes: ["skill", "knowledge", "practical", "portfolio"],
      adaptiveDifficulty: true,
      detailedFeedback: true,
      progressTracking: true
    },
    settings: {
      maxDuration: 3600, // 1 hour
      questionPool: 100,
      confidenceScoring: true
    },
    preview: "Assess skills with adaptive testing",
    difficulty: "intermediate",
    estimatedTime: 45,
    tags: ["assessment", "testing", "evaluation", "feedback"]
  },
  {
    name: "Portfolio Builder",
    description: "Create and showcase digital portfolios",
    category: "Assessment",
    type: "creative",
    config: {
      portfolioTypes: ["project", "skill", "achievement", "learning"],
      mediaTypes: ["images", "videos", "documents", "links"],
      customization: true,
      sharing: true
    },
    settings: {
      maxProjects: 20,
      allowComments: true,
      publicPrivate: true
    },
    preview: "Build digital portfolios to showcase work",
    difficulty: "beginner",
    estimatedTime: 25,
    tags: ["portfolio", "showcase", "assessment", "creative"]
  },
  {
    name: "Progress Tracker",
    description: "Visual progress tracking with goals and milestones",
    category: "Assessment",
    type: "assessment",
    config: {
      trackingTypes: ["skills", "time", "projects", "achievements"],
      visualization: ["charts", "graphs", "badges", "streaks"],
      goalSetting: true,
      reminders: true
    },
    settings: {
      maxGoals: 20,
      weeklyReports: true,
      shareProgress: true
    },
    preview: "Track learning progress visually",
    difficulty: "beginner",
    estimatedTime: 10,
    tags: ["progress", "tracking", "goals", "visualization"]
  },
  {
    name: "Feedback System",
    description: "Comprehensive feedback collection and analysis",
    category: "Assessment",
    type: "assessment",
    config: {
      feedbackTypes: ["peer", "instructor", "self", "automated"],
      ratingScales: [5, 7, 10],
      comments: true,
      analytics: true
    },
    settings: {
      anonymousOption: true,
      followUpQuestions: true,
      exportReports: true
    },
    preview: "Collect and analyze comprehensive feedback",
    difficulty: "intermediate",
    estimatedTime: 15,
    tags: ["feedback", "assessment", "analytics", "evaluation"]
  },

  // ==================== LANGUAGE & COMMUNICATION ====================
  {
    name: "Language Practice",
    description: "Interactive language learning with speaking and writing",
    category: "Language",
    type: "interactive",
    config: {
      skills: ["speaking", "listening", "reading", "writing"],
      speechRecognition: true,
      pronunciationFeedback: true,
      translation: true
    },
    settings: {
      targetLanguages: ["spanish", "french", "german", "chinese"],
      difficultyLevels: 5,
      dailyGoals: true
    },
    preview: "Practice languages with interactive exercises",
    difficulty: "beginner",
    estimatedTime: 20,
    tags: ["language", "speaking", "writing", "practice"]
  },
  {
    name: "Writing Workshop",
    description: "Structured writing practice with peer feedback",
    category: "Language",
    type: "collaborative",
    config: {
      writingTypes: ["essay", "story", "poem", "article"],
      peerReview: true,
      grammarCheck: true,
      styleGuides: true
    },
    settings: {
      minWordCount: 100,
      maxWordCount: 2000,
      revisionHistory: true
    },
    preview: "Improve writing with structured practice",
    difficulty: "intermediate",
    estimatedTime: 30,
    tags: ["writing", "composition", "feedback", "language"]
  },
  {
    name: "Debate Club",
    description: "Structured debate platform with arguments and voting",
    category: "Language",
    type: "collaborative",
    config: {
      debateTypes: ["formal", "informal", "policy", "ethical"],
      timeLimits: true,
      argumentStructure: true,
      voting: true
    },
    settings: {
      maxParticipants: 8,
      debateDuration: 1800, // 30 minutes
      moderation: true
    },
    preview: "Engage in structured debates",
    difficulty: "intermediate",
    estimatedTime: 35,
    tags: ["debate", "argumentation", "speaking", "critical-thinking"]
  },

  // ==================== MATH & LOGIC ====================
  {
    name: "Math Solver",
    description: "Step-by-step math problem solver with explanations",
    category: "Math",
    type: "interactive",
    config: {
      mathTypes: ["algebra", "geometry", "calculus", "statistics"],
      stepByStep: true,
      graphing: true,
      explanations: true
    },
    settings: {
      complexity: ["basic", "intermediate", "advanced"],
      showWork: true,
      practiceMode: true
    },
    preview: "Solve math problems with step-by-step guidance",
    difficulty: "intermediate",
    estimatedTime: 15,
    tags: ["math", "problem-solving", "education", "interactive"]
  },
  {
    name: "Logic Puzzles",
    description: "Interactive logic puzzles with difficulty progression",
    category: "Math",
    type: "assessment",
    config: {
      puzzleTypes: ["sudoku", "logic-grid", "pattern", "deduction"],
      difficultyLevels: 5,
      hints: true,
      timer: true
    },
    settings: {
      maxPuzzles: 50,
      adaptiveDifficulty: true,
      progressTracking: true
    },
    preview: "Solve logic puzzles with progressive difficulty",
    difficulty: "beginner",
    estimatedTime: 20,
    tags: ["logic", "puzzles", "problem-solving", "critical-thinking"]
  },
  {
    name: "Data Visualizer",
    description: "Create charts and graphs from data sets",
    category: "Math",
    type: "interactive",
    config: {
      chartTypes: ["bar", "line", "pie", "scatter", "heatmap"],
      dataImport: ["csv", "json", "manual"],
      customization: true,
      exportFormats: ["png", "svg", "pdf"]
    },
    settings: {
      maxDataPoints: 1000,
      realTimeUpdate: true,
      animations: true
    },
    preview: "Visualize data with interactive charts",
    difficulty: "intermediate",
    estimatedTime: 20,
    tags: ["data", "visualization", "charts", "analytics"]
  }
];

export const COMPONENT_CATEGORIES = [
  {
    name: "Creative Coding",
    icon: "🎨",
    description: "Interactive creative programming and visual art tools",
    color: "#E91E63"
  },
  {
    name: "Social Learning",
    icon: "👥",
    description: "Community-based learning and sharing platforms",
    color: "#FF9800"
  },
  {
    name: "Marketplace",
    icon: "🏪",
    description: "Course marketplace and instructor platforms",
    color: "#4CAF50"
  },
  {
    name: "Virtual Reality",
    icon: "🌍",
    description: "3D virtual worlds and immersive learning environments",
    color: "#9C27B0"
  },
  {
    name: "AI Learning",
    icon: "🤖",
    description: "AI-powered adaptive learning and analytics",
    color: "#2196F3"
  },
  {
    name: "Coding",
    icon: "💻",
    description: "Programming and development tools",
    color: "#4CAF50"
  },
  {
    name: "Technology",
    icon: "🔧",
    description: "Tech tools and infrastructure",
    color: "#2196F3"
  },
  {
    name: "Creative Arts",
    icon: "🎨",
    description: "Artistic and creative expression tools",
    color: "#E91E63"
  },
  {
    name: "Gaming",
    icon: "🎮",
    description: "Game-based learning and entertainment",
    color: "#9C27B0"
  },
  {
    name: "Collaboration",
    icon: "🤝",
    description: "Team and communication tools",
    color: "#FF9800"
  },
  {
    name: "Assessment",
    icon: "📊",
    description: "Testing and evaluation tools",
    color: "#607D8B"
  },
  {
    name: "Language",
    icon: "📚",
    description: "Language and communication tools",
    color: "#795548"
  },
  {
    name: "Math",
    icon: "🔢",
    description: "Mathematics and logic tools",
    color: "#00BCD4"
  }
];

export const COMPONENT_TYPES = [
  {
    name: "Interactive",
    description: "Hands-on tools for active learning",
    icon: "🎯"
  },
  {
    name: "Assessment",
    description: "Testing and evaluation components",
    icon: "📝"
  },
  {
    name: "Collaborative",
    description: "Tools for group work and communication",
    icon: "👥"
  },
  {
    name: "Creative",
    description: "Tools for creative expression and projects",
    icon: "✨"
  }
];

export default LEARNING_COMPONENTS_LIBRARY;
